// src/pages/calendar/Meetings/Meetings.js
import React, { useState } from "react";
import styled from "@emotion/styled";
import { NavLink, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider as MuiDivider,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Breadcrumbs as MuiBreadcrumbs,
  Link,
  Snackbar,
} from "@mui/material";
import {
  Add as AddIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  VideoCall as VideoCallIcon,
  Videocam as VideocamIcon,
  Check as CheckIcon,
  Cancel as CancelIcon,
  CalendarMonth as CalendarIcon,
  Schedule as ScheduleIcon,
  PersonOutline as AttendeeIcon,
} from "@mui/icons-material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useMutation, useQuery } from "@apollo/client";
import { gql } from "@apollo/client";
import { useSpace } from "../../../contexts/SpaceContext";
import { format } from "date-fns";
import SearchBar from "../../components/SearchBar";

// Styled components for consistent UI
const Divider = styled(MuiDivider)(({ theme }) => ({
  margin: theme.spacing(6, 0),
}));
const Breadcrumbs = styled(MuiBreadcrumbs)(({ theme }) => ({
  margin: theme.spacing(2, 0),
}));

// GraphQL Queries and Mutations for Instant Meetings
const GET_INSTANT_MEETINGS = gql`
  query GetInstantMeetings($spaceId: ID!) {
    getInstantMeetings(spaceId: $spaceId) {
      id
      meetingTitle
      meetingUrl
      meetingId
      roomName
      provider
      invitedEmails
      status
      createdAt
      updatedAt
      attendeeUrl
      meetingType
      scheduledAt
      meetingRecordId
      hostToken
    }
  }
`;

const CREATE_INSTANT_MEETING = gql`
  mutation CreateInstantMeeting($input: CreateInstantMeetingInput!) {
    createInstantMeeting(input: $input) {
      id
      meetingTitle
      meetingUrl
      meetingId
      roomName
      provider
      invitedEmails
      status
      createdAt
      updatedAt
      attendeeUrl
      meetingType
      scheduledAt
      meetingRecordId
      hostToken
    }
  }
`;

const DELETE_INSTANT_MEETING = gql`
  mutation DeleteInstantMeeting($meetingId: String!) {
    deleteInstantMeeting(meetingId: $meetingId) {
      success
      message
    }
  }
`;

// GraphQL Queries and Mutations for Widget Bookings
const GET_WIDGET_BOOKINGS = gql`
  query GetWidgetBookings($spaceId: ID!, $date: String) {
    getWidgetBookings(spaceId: $spaceId, date: $date) {
      id
      widgetId
      customerName
      customerEmail
      date
      timeStart
      timeEnd
      status
      notes
      meetingLink
      hostMeetingLink
      createdAt
    }
  }
`;

const UPDATE_WIDGET_BOOKING_STATUS = gql`
  mutation UpdateWidgetBookingStatus($id: ID!, $status: String!) {
    updateWidgetBookingStatus(id: $id, status: $status) {
      id
      status
    }
  }
`;

const DELETE_WIDGET_BOOKING = gql`
  mutation DeleteWidgetBooking($id: ID!) {
    deleteWidgetBooking(id: $id) {
      success
      message
    }
  }
`;

const Meetings = () => {
  const location = useLocation();
  const { currentSpace } = useSpace();
  const queryParams = new URLSearchParams(location.search);
  const currentSpaceId = queryParams.get("spaceId") || currentSpace?.id;

  // State for Instant Meetings
  const [openDialog, setOpenDialog] = useState(false);
  const [meetingTitle, setMeetingTitle] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [emails, setEmails] = useState([]);
  const [copySuccess, setCopySuccess] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [meetingMode, setMeetingMode] = useState("instant");
  const [scheduledDateTime, setScheduledDateTime] = useState(dayjs().add(1, 'hour'));

  // Alert state for Snackbar
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // GraphQL hooks for Instant Meetings
  const { data, loading, error, refetch } = useQuery(GET_INSTANT_MEETINGS, {
    variables: { spaceId: currentSpaceId },
    skip: !currentSpaceId,
  });

  const [createMeeting, { loading: creating }] = useMutation(
    CREATE_INSTANT_MEETING,
    {
      onCompleted: (data) => {
        const created = data.createInstantMeeting;
        setOpenDialog(false);
        setMeetingTitle("");
        setEmails([]);
        setEmailInput("");
        refetch();

        // Auto-copy the host meeting URL
        handleCopyLink(created.meetingUrl);

        if (meetingMode === "instant") {
          // Instant: auto-open in new tab
          window.open(created.meetingUrl, "_blank");
        } else {
          // Scheduled: show success alert with scheduled time (do NOT auto-open)
          const scheduledTime = created.scheduledAt
            ? format(new Date(created.scheduledAt), "PPpp")
            : "";
          setAlert({
            open: true,
            message: `Meeting scheduled for ${scheduledTime}. Host link copied to clipboard.`,
            severity: "success",
          });
        }

        // Reset mode
        setMeetingMode("instant");
        setScheduledDateTime(dayjs().add(1, 'hour'));
      },
      onError: (error) => {
        console.error("Error creating meeting:", error);
        setAlert({
          open: true,
          message: `Error creating meeting: ${error.message}`,
          severity: "error",
        });
      },
    }
  );

  const [deleteMeeting] = useMutation(DELETE_INSTANT_MEETING, {
    onCompleted: () => {
      refetch();
    },
    onError: (error) => {
      console.error("Error deleting meeting:", error);
    },
  });

  // GraphQL hooks for Widget Bookings
  const {
    data: widgetBookingsData,
    loading: widgetBookingsLoading,
    error: widgetBookingsError,
    refetch: refetchWidgetBookings,
  } = useQuery(GET_WIDGET_BOOKINGS, {
    variables: { spaceId: currentSpaceId },
    skip: !currentSpaceId,
  });

  const [updateWidgetBookingStatus] = useMutation(
    UPDATE_WIDGET_BOOKING_STATUS,
    {
      onCompleted: () => {
        refetchWidgetBookings();
      },
      onError: (error) => {
        console.error("Error updating booking status:", error);
        setAlert({
          open: true,
          message: `Error updating booking: ${error.message}`,
          severity: "error",
        });
      },
    }
  );

  const [deleteWidgetBooking] = useMutation(DELETE_WIDGET_BOOKING, {
    onCompleted: () => {
      refetchWidgetBookings();
    },
    onError: (error) => {
      console.error("Error deleting booking:", error);
      setAlert({
        open: true,
        message: `Error deleting booking: ${error.message}`,
        severity: "error",
      });
    },
  });

  // Handlers for Instant Meetings
  const handleOpenDialog = (mode) => {
    setMeetingMode(mode);
    setOpenDialog(true);
    setMeetingTitle(`Meeting ${new Date().toLocaleString()}`);
    setScheduledDateTime(dayjs().add(1, 'hour'));
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setMeetingTitle("");
    setEmails([]);
    setEmailInput("");
    setMeetingMode("instant");
    setScheduledDateTime(dayjs().add(1, 'hour'));
  };

  const handleAddEmail = () => {
    const trimmedEmail = emailInput.trim();
    if (trimmedEmail && !emails.includes(trimmedEmail)) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(trimmedEmail)) {
        setEmails([...emails, trimmedEmail]);
        setEmailInput("");
      } else {
        setAlert({
          open: true,
          message: "Please enter a valid email address",
          severity: "warning",
        });
      }
    }
  };

  const handleRemoveEmail = (emailToRemove) => {
    setEmails(emails.filter((email) => email !== emailToRemove));
  };

  const handleCreateMeeting = () => {
    if (!currentSpaceId) {
      setAlert({ open: true, message: "Please select a space", severity: "warning" });
      return;
    }

    const input = {
      spaceId: currentSpaceId,
      meetingTitle: meetingTitle || `Meeting ${new Date().toLocaleString()}`,
      meetingType: meetingMode,
    };

    if (meetingMode === "scheduled") {
      if (!scheduledDateTime || !scheduledDateTime.isValid()) {
        setAlert({ open: true, message: "Please select a valid date and time", severity: "warning" });
        return;
      }
      if (scheduledDateTime.isBefore(dayjs())) {
        setAlert({ open: true, message: "Scheduled time must be in the future", severity: "warning" });
        return;
      }
      input.scheduledAt = scheduledDateTime.toISOString();
      input.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    }

    if (emails.length > 0) {
      input.invitedEmails = emails;
    }

    createMeeting({ variables: { input } });
  };

  const handleCopyLink = (url) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopySuccess(true);
      setAlert({
        open: true,
        message: "Meeting link copied to clipboard!",
        severity: "success",
      });
    });
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
    setCopySuccess(false);
  };

  const handleDeleteMeeting = (meetingId) => {
    if (window.confirm("Are you sure you want to delete this meeting?")) {
      deleteMeeting({ variables: { meetingId } });
    }
  };

  const handleJoinMeeting = (url) => {
    window.open(url, "_blank");
  };

  // Handlers for Widget Bookings
  const handleConfirmBooking = (bookingId) => {
    updateWidgetBookingStatus({
      variables: { id: bookingId, status: "confirmed" },
    });
  };

  const handleCancelBooking = (bookingId) => {
    if (window.confirm("Are you sure you want to cancel this booking?")) {
      updateWidgetBookingStatus({
        variables: { id: bookingId, status: "canceled" },
      });
    }
  };

  const handleDeleteWidgetBooking = (bookingId) => {
    if (window.confirm("Are you sure you want to delete this booking?")) {
      deleteWidgetBooking({ variables: { id: bookingId } });
    }
  };

  const formatTime = (time24) => {
    if (!time24) return "";
    const [hours, minutes] = time24.split(":").map(Number);
    const period = hours >= 12 ? "PM" : "AM";
    const hours12 = hours % 12 || 12;
    return `${hours12}:${minutes.toString().padStart(2, "0")} ${period}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed":
        return "success";
      case "pending":
        return "warning";
      case "canceled":
        return "default";
      default:
        return "default";
    }
  };

  if (!currentSpaceId) {
    return (
      <React.Fragment>
        <Helmet title="Meetings" />
        <Alert severity="warning">
          Please select a space to view meetings.
        </Alert>
      </React.Fragment>
    );
  }

  // Filter meetings based on search query
  const filteredMeetings =
    data?.getInstantMeetings?.filter((meeting) => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        meeting.meetingTitle?.toLowerCase().includes(query) ||
        meeting.meetingUrl?.toLowerCase().includes(query) ||
        meeting.roomName?.toLowerCase().includes(query) ||
        meeting.invitedEmails?.some((email) =>
          email.toLowerCase().includes(query)
        ) ||
        meeting.status?.toLowerCase().includes(query) ||
        meeting.meetingType?.toLowerCase().includes(query)
      );
    }) || [];

  // Filter widget bookings based on search query
  const filteredWidgetBookings =
    widgetBookingsData?.getWidgetBookings?.filter((booking) => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        booking.customerName?.toLowerCase().includes(query) ||
        booking.customerEmail?.toLowerCase().includes(query) ||
        booking.notes?.toLowerCase().includes(query) ||
        booking.status?.toLowerCase().includes(query)
      );
    }) || [];

  return (
    <React.Fragment>
      <Helmet title="Meetings" />

      <Grid justifyContent="space-between" container spacing={6}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            Meetings
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to={`/spaces/${currentSpaceId}`}>
              {currentSpace?.name || "Spaces"}
            </Link>
            <Link
              component={NavLink}
              to={`/calendar/dashboard?spaceId=${currentSpaceId}`}
            >
              Calendar
            </Link>
            <Typography color="text.primary">Meetings</Typography>
          </Breadcrumbs>
        </Grid>
        <Grid item>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog("instant")}
            >
              Start Instant Meeting
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<ScheduleIcon />}
              onClick={() => handleOpenDialog("scheduled")}
            >
              Schedule Meeting
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Divider my={6} />

      {/* Search bar */}
      <SearchBar
        value={searchQuery}
        onChange={handleSearchChange}
        placeholder="Search meetings..."
        containerSx={{
          borderBottom: "1px solid rgba(224, 224, 224, 0.4)",
          mb: 2,
        }}
      />

      {/* Instant Meetings Section */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        Meetings
      </Typography>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading meetings: {error.message}
        </Alert>
      )}

      {data && data.getInstantMeetings && (
        <Paper sx={{ mb: 4 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Meeting Title</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Created / Scheduled</TableCell>
                <TableCell>Meeting URL</TableCell>
                <TableCell>Invited Emails</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMeetings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ py: 4 }}
                    >
                      {searchQuery
                        ? "No meetings match your search."
                        : 'No meetings yet. Click "Start Instant Meeting" or "Schedule Meeting" to create one.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredMeetings.map((meeting) => (
                  <TableRow key={meeting.id}>
                    <TableCell>{meeting.meetingTitle}</TableCell>
                    <TableCell>
                      <Chip
                        label={meeting.meetingType === "scheduled" ? "Scheduled" : "Instant"}
                        color={meeting.meetingType === "scheduled" ? "info" : "primary"}
                        size="small"
                        icon={meeting.meetingType === "scheduled" ? <ScheduleIcon /> : <VideoCallIcon />}
                      />
                    </TableCell>
                    <TableCell>
                      {meeting.meetingType === "scheduled" && meeting.scheduledAt
                        ? format(new Date(meeting.scheduledAt), "PPpp")
                        : meeting.createdAt
                        ? format(new Date(meeting.createdAt), "PPpp")
                        : "N/A"}
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}
                      >
                        <Tooltip title="Copy Host Link">
                          <IconButton
                            size="small"
                            onClick={() => handleCopyLink(meeting.meetingUrl)}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Typography variant="caption" color="textSecondary">Host</Typography>
                        {meeting.attendeeUrl && (
                          <>
                            <Tooltip title="Copy Attendee Link">
                              <IconButton
                                size="small"
                                color="secondary"
                                onClick={() => handleCopyLink(meeting.attendeeUrl)}
                              >
                                <AttendeeIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Typography variant="caption" color="textSecondary">Attendee</Typography>
                          </>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {meeting.invitedEmails &&
                      meeting.invitedEmails.length > 0 ? (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {meeting.invitedEmails.slice(0, 2).map((email) => (
                            <Chip key={email} label={email} size="small" />
                          ))}
                          {meeting.invitedEmails.length > 2 && (
                            <Chip
                              label={`+${
                                meeting.invitedEmails.length - 2
                              } more`}
                              size="small"
                            />
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          None
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={meeting.status}
                        color={
                          meeting.status === "active" ? "success" : "default"
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Join Meeting">
                        <IconButton
                          color="primary"
                          onClick={() => handleJoinMeeting(meeting.meetingUrl)}
                        >
                          <VideoCallIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Meeting">
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteMeeting(meeting.meetingId)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Widget Bookings Section */}
      <Divider my={6} />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 2,
        }}
      >
        <CalendarIcon color="primary" />
        <Typography variant="h6">Widget Bookings</Typography>
      </Box>

      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Bookings made by customers through your website widget
      </Typography>

      {widgetBookingsLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {widgetBookingsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading widget bookings: {widgetBookingsError.message}
        </Alert>
      )}

      {widgetBookingsData && widgetBookingsData.getWidgetBookings && (
        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Customer Name</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Time</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell>Meeting</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredWidgetBookings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ py: 4 }}
                    >
                      {searchQuery
                        ? "No widget bookings match your search."
                        : "No widget bookings yet. Bookings will appear here when customers book through your website widget."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredWidgetBookings.map((booking) => (
                  <TableRow key={booking.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {booking.customerName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {booking.date
                        ? format(new Date(booking.date + "T00:00:00"), "PP")
                        : "N/A"}
                    </TableCell>
                    <TableCell>
                      {formatTime(booking.timeStart)} -{" "}
                      {formatTime(booking.timeEnd)}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 180,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {booking.customerEmail}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={booking.status}
                        color={getStatusColor(booking.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{
                          maxWidth: 150,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {booking.notes || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        title={
                          (booking.hostMeetingLink || booking.meetingLink)
                            ? "Join Meeting"
                            : "No meeting link"
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() =>
                              window.open(booking.hostMeetingLink || booking.meetingLink, "_blank")
                            }
                            disabled={!(booking.hostMeetingLink || booking.meetingLink)}
                          >
                            <VideocamIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      {booking.status === "pending" && (
                        <Tooltip title="Confirm Booking">
                          <IconButton
                            color="success"
                            size="small"
                            onClick={() => handleConfirmBooking(booking.id)}
                          >
                            <CheckIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {booking.status !== "canceled" && (
                        <Tooltip title="Cancel Booking">
                          <IconButton
                            color="warning"
                            size="small"
                            onClick={() => handleCancelBooking(booking.id)}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete Booking">
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleDeleteWidgetBooking(booking.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Create Meeting Dialog */}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {meetingMode === "scheduled" ? "Schedule Meeting" : "Start Instant Meeting"}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label="Meeting Title (Optional)"
                value={meetingTitle}
                onChange={(e) => setMeetingTitle(e.target.value)}
                sx={{ mb: 3 }}
              />

              {meetingMode === "scheduled" && (
                <DateTimePicker
                  label="Schedule Date & Time"
                  value={scheduledDateTime}
                  onChange={(newValue) => setScheduledDateTime(newValue)}
                  minDateTime={dayjs()}
                  slotProps={{
                    textField: { fullWidth: true, sx: { mb: 3 } },
                  }}
                />
              )}

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Invite Users by Email (Optional):
              </Typography>
              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter email address"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddEmail();
                    }
                  }}
                />
                <Button variant="outlined" onClick={handleAddEmail}>
                  Add
                </Button>
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {emails.map((email) => (
                  <Chip
                    key={email}
                    label={email}
                    onDelete={() => handleRemoveEmail(email)}
                  />
                ))}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              onClick={handleCreateMeeting}
              variant="contained"
              color="primary"
              disabled={creating}
            >
              {creating ? (
                <CircularProgress size={24} />
              ) : meetingMode === "scheduled" ? (
                "Schedule Meeting"
              ) : (
                "Start Meeting"
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </LocalizationProvider>

      {/* Alert Snackbar */}
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
      >
        <Alert onClose={handleCloseAlert} severity={alert.severity}>
          {alert.message}
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
};

export default Meetings;
